import { useMemo, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutGrid } from "lucide-react";
import UserProfileCard from "../../components/UserProfileCard";
import ReportListItem, {
  type ReportItem,
} from "../../components/Reports/ReportListItem";
import { FolderOpen } from "lucide-react";
import ReportSortBar, {
  type SortOptionValue,
} from "../../components/Reports/ReportSortBar";
import trackProgressIcon from "../../assets/track-progress.svg";
import defaultProjectImg from "../../assets/Projects/project1.png";
import { getAllProjects } from "../../api/Projects";
import type { Project } from "../../utils/interface/project";
import { useAuth } from "../../contexts/AuthContext";
import Pagination from "../../components/UI/Pagination";
import type { Pagination as PaginationType } from "../../utils/interface/pagination";

function formatCompletedDate(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

type AuditItem = ReportItem & { sortDate: string };

function getReportFileUrl(
  project: Project & {
    ifcModelReport?: string | { url?: string; downloadUrl?: string } | null;
  },
): string | undefined {
  const raw = project.ifcModelReport;
  if (typeof raw === "string") return raw || undefined;
  const obj = raw as { url?: string; downloadUrl?: string } | null | undefined;
  return obj?.url ?? obj?.downloadUrl ?? undefined;
}

function projectToReportItem(project: Project): AuditItem {
  const dateRaw = project.createdAt ?? project.updatedAt ?? "";
  const sortDate = project.updatedAt || project.createdAt;
  return {
    id: project.id,
    title: project.name,
    description: project.summary || "",
    completedDate: dateRaw ? formatCompletedDate(dateRaw) : "",
    image: (project.projectThumbnailPreview ?? project.projectThumbnail) || defaultProjectImg,
    reportFileUrl: getReportFileUrl(project),
    sortDate,
  };
}

const TrackAudits = () => {
  const navigate = useNavigate();
  const { userDetails } = useAuth();
  const [sortValue, setSortValue] = useState<SortOptionValue>("date-desc");
  const [audits, setAudits] = useState<AuditItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationType | null>(null);

  const sortOrderParam: "ASC" | "DESC" =
    sortValue === "date-asc" ? "ASC" : "DESC";

  const PAGE_SIZE = 5;

  const fetchProjects = useCallback(
    async (page: number = currentPage) => {
      setLoading(true);
      setError(null);
      try {
        const resp = await getAllProjects(page, PAGE_SIZE, undefined, sortOrderParam);
        const projects: Project[] = resp?.projects ?? [];
        setAudits(projects.map(projectToReportItem));
        setPagination(resp?.pagination ?? null);
      } catch (err) {
        setError("Failed to load projects.");
        setAudits([]);
        setPagination(null);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- currentPage is passed as default arg; including it would recreate callback every page change
    [sortOrderParam, currentPage],
  );

  useEffect(() => {
    fetchProjects(currentPage);
  }, [currentPage, fetchProjects]);

  const sortedAudits = useMemo(() => [...audits], [audits]);

  return (
    <div>
      <UserProfileCard />

      <div className="heading-wrap mt-2">
        <h1 className="text-[34px] lg:text-[38px] xl:text-[56px] font-display font-weight-300 text-white mb-2">
          Track Audits
        </h1>
        <p className="text-white font-weight-300 text-[14px] lg:text-[16px] xl:text-[18px]">
          {userDetails?.type === "2"
            ? "Check the progress of your IFC file uploads"
            : "Check and update the progress of users IFC file uploads"}
        </p>
      </div>

      <div className="mt-6 mb-4">
        <ReportSortBar
          value={sortValue}
          onChange={(v) => {
            setSortValue(v);
            setCurrentPage(1);
          }}
        />
      </div>

      <div className="downoad-project overflow-hidden track-audits ">
        {loading && (
          <div className="flex flex-col items-center justify-center">
            <div className="relative">
              <div className="w-20 h-20 border-[3px] border-[#E761A0]/20 border-t-[#E761A0] rounded-full animate-spin" />
              <LayoutGrid className="w-8 h-8 text-[#E761A0] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
            </div>
            <p className="text-white/90 text-base mt-5 font-medium">
              Loading audits...
            </p>
          </div>
        )}
        {error && <p className="py-8 text-red-400 text-center">{error}</p>}
        {!loading && !error && sortedAudits.length === 0 && (
          // <p className="py-8 text-white/70 text-center">No projects to show.</p>
          <div className="text-center py-20 px-6">
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-2xl border border-[#E761A0]/30 bg-[#E761A0]/5 mb-6">
            <FolderOpen className="w-12 h-12 text-[#E761A0]" />
          </div>
          <h3 className="text-white text-lg font-semibold mb-2">
          No projects to show
          </h3>

        </div>
        )}
        {!loading &&
          !error &&
          sortedAudits.map((audit, i) => (
            <ReportListItem
              key={audit.id}
              report={audit}
              isLast={i === sortedAudits.length - 1}
              userType={
                userDetails?.type != null ? String(userDetails.type) : undefined
              }
              onDownload={(r) => navigate(`/ifc-processing/${r.id}`)}
              downloadButtonIcon={trackProgressIcon}
              buttonLabel={
                userDetails?.type === "0" ? "Update Progress" : "Track Progress"
              }
              dateLabel="Created At"
              useNavigateForAdmin
              useNavigateForUserType2
            />
          ))}
        {!loading && !error && pagination && pagination.totalPages > 1 && (() => {
          const totalCount = pagination.totalItems ?? pagination.currentlyShowing;
          const showingFrom = Math.min((currentPage - 1) * pagination.limit + 1, totalCount);
          const showingTo = Math.min(currentPage * pagination.limit, totalCount);
          return (
            <div className="mt-6">
              <Pagination
                currentPage={currentPage}
                totalPages={pagination.totalPages}
                onPageChange={setCurrentPage}
                totalItems={totalCount}
                itemsPerPage={pagination.limit}
                showingFrom={showingFrom}
                showingTo={showingTo}
              />
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default TrackAudits;
